import { ArrowDownOutlined, DislikeOutlined } from "@ant-design/icons";
import {
  CopyContentType,
  Icon,
  Button as IndigoButton,
  LoadingPlaceholder,
  Tooltip,
  useCopyToClipboard,
} from "@indigo-components";
import {
  ChatAttachmentTile,
  ChatPromptFile,
  ChatPromptText,
} from "@indigo-components/components/Chat";
import { Button } from "antd";
import {
  CSSProperties,
  FC,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Components } from "react-markdown";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { useAnimatedUnmount } from "~/hooks/useAnimatedUnmount";
import { type ChatContext } from "~/hooks/useChatbotContext";
import type { ChatbotLinkContext } from "~/pages/appointments/components/chatbotLinks";
import { IndyScanIndicator } from "~/pages/appointments/components/IndyScanIndicator";
import {
  type DataBlockMap,
  dataBlockKind,
  MARKDOWN_BLOCK_KIND,
  spliceAllDataBlocks,
  type ToolStep,
  type UseAgentChatReturn,
} from "~/pages/appointments/hooks/useAgentChat";
import { AuthUserGroup } from "~/types/auth";
import { ChatbotEmptyState } from "./ChatbotEmptyState";
import { ChatMessageBody } from "./ChatMessageBody";
import {
  LONG_MESSAGE_PREVIEW_CHARS,
  LONG_MESSAGE_THRESHOLD_CHARS,
  MAX_INPUT_CHARS,
  renderAttachmentIcon,
} from "./chatSurfaceShared";
import {
  IndyFeedbackModal,
  type IndyFeedbackTarget,
} from "./IndyFeedbackModal";
import { LongMessageModal, type LongMessageSource } from "./LongMessageModal";

const PHASE_LABELS: Record<UseAgentChatReturn["phase"], string> = {
  idle: "",
  fetching_context: "Thinking...",
  generating: "Generating response...",
  tool_executing: "Looking up additional records...",
  done: "",
  error: "An error occurred",
};

/** The one line box every row in the activity block sits on — the tool steps
 *  and the reading-indicator row alike. It is also the step icon well's height
 *  (so the icon centres on the first line, not on a wrapped block) and the
 *  indicator row's min-height: without that floor the row is only as tall as
 *  the glasses, so the phase label appearing grew it to this same 16px and
 *  re-centred the glasses ~2px lower — a visible twitch on every state change. */
const ACTIVITY_LINE_HEIGHT = 16;

/** How long the reading indicator takes to collapse once the answer starts.
 *  Fed to the CSS as a custom property so the timing has one definition. */
const INDICATOR_EXIT_MS = 180;

interface ToolStepsListProps {
  steps: ToolStep[];
  /**
   * Animate rows as they arrive in the live list. History renders settled
   * rows immediately, without replaying the entry animation.
   */
  animateEntry?: boolean;
}

export const ToolStepsList: FC<ToolStepsListProps> = ({
  steps,
  animateEntry,
}) => {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  return (
    <div className="mb-1.5 space-y-0.5">
      <div id={listId} className="space-y-0.5">
        {steps.map((step, i) =>
          expanded || i === steps.length - 1 ? (
            <div
              key={step.callId ? `${step.callId}:${step.tool}` : i}
              // items-start, not items-center: a step whose label wraps would
              // otherwise float its icon to the middle of the whole block. The
              // explicit line-height plus the matching icon well below pin the icon
              // to the centre of the FIRST line, however many lines follow.
              className={`flex items-start gap-1.5${animateEntry ? " indy-tool-step" : ""}`}
              style={{
                color: "var(--indigo-text-secondary)",
                fontSize: 11,
                lineHeight: `${ACTIVITY_LINE_HEIGHT}px`,
              }}
            >
              <span
                className="flex-none flex items-center"
                style={{ height: ACTIVITY_LINE_HEIGHT }}
              >
                {/* Keyed on status so the swap is a remount: the check lands with a
              small pop instead of cutting over the spinner. Only in the live
              list — see `animateEntry`.
              Both states at one size: the spinner was 12px against the check's
              14px, so every step nudged sideways as it completed. */}
                <span
                  key={animateEntry ? step.status : undefined}
                  className={animateEntry ? "indy-tool-step__icon" : undefined}
                >
                  {step.status === "running" ? (
                    <Icon name="spinner" sizeX={14} sizeY={14} />
                  ) : (
                    <Icon name="check-circle" sizeX={14} sizeY={14} />
                  )}
                </span>
              </span>
              <span>
                {step.status === "done"
                  ? step.result || step.label
                  : step.label}
              </span>
            </div>
          ) : null,
        )}
      </div>
      {steps.length > 1 && (
        <IndigoButton
          type="button"
          variant="link"
          size="xs"
          className="!h-auto !w-full !justify-start !gap-1 !rounded-none !border-0 !px-0 !py-1 !text-[11px] !leading-4 !font-normal !text-[var(--indigo-text-secondary)] hover:!no-underline"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? "Collapse" : "See more"}
          <Icon
            name="right-chevron"
            size="xs"
            className={expanded ? "-rotate-90" : undefined}
          />
        </IndigoButton>
      )}
    </div>
  );
};

type ChatMessage = UseAgentChatReturn["messages"][number];

function hasTypedBlocks(blocks: DataBlockMap | undefined): boolean {
  if (!blocks) return false;
  for (const block of blocks.values()) {
    if (dataBlockKind(block) !== MARKDOWN_BLOCK_KIND) return true;
  }
  return false;
}

/**
 * A persisted assistant message: rendered content + hover Copy / thumbs-down
 * buttons. Thumbs-down files an internal Linear ticket, so it renders only when
 * `onReport` is given (the list withholds it from doctors).
 *
 * Copy normally pulls from the *rendered* DOM (via the clipboard hook's
 * `targetRef`). The hook writes both a `text/html` representation (so rich
 * targets keep bold/italic styling) and a plain-text one derived from the
 * DOM — so neither paste path leaks literal Markdown markers like `**` the
 * way copying the raw source did.
 *
 * A reply carrying entity components is the exception, and drops to plain
 * text: the DOM under a card is layout, not content, so both flavors would
 * otherwise paste card scaffolding (and `extractHTML` short-circuits on the
 * first `<table>`, which would silently drop everything else in a mixed
 * reply). Its text comes from splicing every block's markdown fallback into
 * the message — the same text the server persisted and Slack would show.
 */
const AssistantMessage: FC<{
  msg: ChatMessage;
  markdownComponents: Components;
  linkContext: ChatbotLinkContext;
  onReport?: (target: IndyFeedbackTarget) => void;
  streaming?: boolean;
  activity?: ReactNode;
}> = ({
  msg,
  markdownComponents,
  linkContext,
  onReport,
  streaming = false,
  activity,
}) => {
  const typedBlocks = hasTypedBlocks(msg.dataBlocks);
  const customTextExtractor = useMemo(
    () =>
      typedBlocks && msg.dataBlocks
        ? () => spliceAllDataBlocks(msg.content, msg.dataBlocks ?? new Map())
        : undefined,
    [typedBlocks, msg.content, msg.dataBlocks],
  );
  // The ticket quotes the same text Copy's plain-text path would produce.
  const handleReport = () =>
    onReport?.({
      conversationId: msg.conversationId,
      messageId: msg.messageId,
      assistantMessage: customTextExtractor
        ? customTextExtractor()
        : msg.content,
    });
  const { targetRef, isCopied, copy } = useCopyToClipboard({
    contentType: typedBlocks ? CopyContentType.TEXT : CopyContentType.HTML,
    customTextExtractor,
  });

  return (
    <div className="w-full group relative px-3">
      {activity}
      {msg.toolSteps && msg.toolSteps.length > 0 && (
        <ToolStepsList steps={msg.toolSteps} animateEntry={streaming} />
      )}
      {/* Same token as the user's ChatPromptText bubble, so replies follow
          the Portal text-size setting (Tailwind's text-sm is a fixed 14px). */}
      <div
        ref={targetRef}
        className="text-[length:var(--indigo-font-size-sm)] chatbot-markdown"
        style={{ color: "var(--indigo-text-primary)", minWidth: 0 }}
      >
        <ChatMessageBody
          streaming={streaming}
          content={msg.content}
          dataBlocks={msg.dataBlocks}
          markdownComponents={markdownComponents}
          linkContext={linkContext}
        />
      </div>
      {msg.stopped && (
        <div
          className="text-xs italic mt-1"
          style={{ color: "var(--indigo-text-tertiary)" }}
        >
          Stopped by you
        </div>
      )}
      {!streaming && (
        <div className="absolute left-3 flex items-center gap-1 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Tooltip
            content={isCopied ? "Copied!" : "Copy"}
            placement="top"
            trigger="mouseenter"
            // Stay visible through the click so the "Copied!" swap is seen.
            hideOnClick={false}
          >
            <Button
              type="text"
              size="small"
              icon={
                isCopied ? (
                  <Icon name="check" size="xs" />
                ) : (
                  <Icon name="copy" size="xs" />
                )
              }
              onClick={() => void copy()}
              style={{
                color: "var(--indigo-text-secondary)",
                width: 20,
                height: 20,
                minWidth: 20,
              }}
            />
          </Tooltip>
          {onReport && (
            <Tooltip
              content="Report an issue"
              placement="top"
              trigger="mouseenter"
            >
              <Button
                type="text"
                size="small"
                icon={<DislikeOutlined style={{ fontSize: 12 }} />}
                onClick={handleReport}
                style={{
                  color: "var(--indigo-text-secondary)",
                  width: 20,
                  height: 20,
                  minWidth: 20,
                }}
              />
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};

interface MessageListProps {
  layout: "panel" | "fullscreen";
  /** Fullscreen at phone widths — see `IndyChatSurfaceProps.mobile`. */
  mobile?: boolean;
  chat: UseAgentChatReturn;
  context: ChatContext | null;
  userGreetingName: string | null;
  markdownComponents: Components;
  linkContext: ChatbotLinkContext;
  onSelectSuggestion: (prompt: string) => void;
}

export const MessageList: FC<MessageListProps> = ({
  layout,
  mobile = false,
  chat,
  context,
  userGreetingName,
  markdownComponents,
  linkContext,
  onSelectSuggestion,
}) => {
  const {
    messages,
    conversationId,
    streamingContent,
    streamingMessageKey,
    streamingDataBlocks,
    streamingToolSteps,
    phase,
    isStreaming,
    isCompacting,
    isLoadingHistory,
    error,
    errorSource,
    warning,
    retryLoad,
  } = chat;

  const fullscreen = layout === "fullscreen";

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [longMessage, setLongMessage] = useState<LongMessageSource | null>(
    null,
  );
  const [dismissedWarning, setDismissedWarning] = useState<string | null>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [feedbackTarget, setFeedbackTarget] =
    useState<IndyFeedbackTarget | null>(null);
  // Thumbs-down feedback is internal (files a Linear ticket); doctors don't
  // get it — same gate as the MRR entry "Report bug" (`showFileTicketAction`).
  const { userGroup } = useAuthSession();
  const canReport = userGroup !== AuthUserGroup.DOCTOR;

  // ── Reading-indicator exit ──
  // The indicator used to unmount the instant the first token landed, so the
  // tool steps under it jumped up a row. Hold it mounted for one collapse:
  // its wrapper animates to zero height and the steps ride up with it. The
  // exit-through-unmount protocol is the shared hook — notably its DERIVED
  // `exiting`: the stored flag it replaces could survive an aborted stream
  // and mount the next stream's indicator already collapsed.
  const showStreaming = isStreaming && phase !== "idle";
  const showIndicator = showStreaming && !streamingContent;
  const { mounted: indicatorMounted, exiting: indicatorExiting } =
    useAnimatedUnmount(showIndicator, INDICATOR_EXIT_MS);

  // One keyed assistant row survives the live -> completed transition so
  // reference images keep their mounted component and signed URL.
  const displayMessages: ChatMessage[] = showStreaming
    ? [
        ...messages,
        {
          conversationId,
          role: "assistant",
          renderKey: streamingMessageKey ?? `progress:${conversationId}`,
          content: streamingContent,
          dataBlocks: streamingDataBlocks,
          toolSteps: streamingToolSteps,
          timestamp: new Date(0),
        },
      ]
    : messages;
  const streamingActivity = indicatorMounted && (
    <div
      className={`indy-loader-collapse${indicatorExiting ? " is-exiting" : ""}`}
      style={
        {
          "--indy-loader-exit": `${INDICATOR_EXIT_MS}ms`,
        } as CSSProperties
      }
    >
      <div className="indy-loader-collapse__inner">
        <div
          // mb-1.5 matches ToolStepsList's own bottom margin, so
          // the glasses and the steps beneath read as one activity
          // block.
          className="flex items-center gap-2 mb-1.5 text-xs"
          style={{
            color: "var(--indigo-text-secondary)",
            minHeight: ACTIVITY_LINE_HEIGHT,
          }}
        >
          {/* 28, not 22: 22 was tuned against the old Original,
                          which was missing the ~19% canvas padding every other
                          variant carries, so it drew larger than it should
                          have. With that fixed the same box renders the
                          glasses at 81% of the size the choice was made
                          against. */}
          <IndyScanIndicator size={28} />
          {/* The steps list already names the tool that is
                          running, so a generic phase label beside it just says
                          the same thing twice. Let the glasses carry the
                          "still working" signal and the steps carry the
                          detail. */}
          {streamingToolSteps.length === 0 && PHASE_LABELS[phase] && (
            <span>{PHASE_LABELS[phase]}</span>
          )}
        </div>
      </div>
    </div>
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesContentRef = useRef<HTMLDivElement>(null);
  // Whether the reader is at the bottom, as of the last scroll.
  const pinnedRef = useRef(true);
  const scrollPositionsRef = useRef(new Map<string, number>());

  // Track conversationId to pick instant vs smooth scroll.
  const prevConversationIdRef = useRef(conversationId);
  const mountedRef = useRef(false);
  const prevLastMessageIdRef = useRef<string | undefined>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    const switched = prevConversationIdRef.current !== conversationId;
    const justMounted = !mountedRef.current;
    prevConversationIdRef.current = conversationId;
    mountedRef.current = true;

    // Detect prepend (load-more): last message unchanged but array grew.
    const lastMsgId = messages[messages.length - 1]?.messageId;
    const wasPrepend =
      !switched &&
      lastMsgId !== undefined &&
      lastMsgId === prevLastMessageIdRef.current &&
      messages.length > 0;
    prevLastMessageIdRef.current = lastMsgId;

    // Skip auto-scroll when older messages were prepended at the top.
    if (wasPrepend) return;

    if (switched) {
      const savedPos = scrollPositionsRef.current.get(conversationId);
      if (savedPos !== undefined) {
        requestAnimationFrame(() => {
          const container = messagesContainerRef.current;
          if (container) container.scrollTop = savedPos;
        });
        return;
      }
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
      return;
    }

    if (justMounted) {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
      return;
    }

    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, streamingContent, conversationId]);

  // ── Track scroll position to show/hide scroll-to-bottom button ──
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      setShowScrollToBottom(distanceFromBottom > 100);
      pinnedRef.current = distanceFromBottom <= 100;
      scrollPositionsRef.current.set(conversationId, container.scrollTop);
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [conversationId]);

  // Resizes fire no scroll event: queued chips shrink the list from below,
  // and the compaction row grows it. Keep a reader at the bottom there.
  useEffect(() => {
    const container = messagesContainerRef.current;
    const content = messagesContentRef.current;
    if (!container || !content) return;
    const observer = new ResizeObserver(() => {
      if (pinnedRef.current) container.scrollTop = container.scrollHeight;
    });
    observer.observe(container);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const handleCopy = useCallback((content: string, idx: number) => {
    void navigator.clipboard.writeText(content);
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex(null), 1500);
  }, []);

  const handleEditStart = useCallback(
    (idx: number, content: string) => {
      if (isStreaming) return;
      setEditingIndex(idx);
      setEditValue(content);
    },
    [isStreaming],
  );

  const handleEditCancel = useCallback(() => {
    setEditingIndex(null);
    setEditValue("");
  }, []);

  const handleEditSend = useCallback(() => {
    const trimmed = editValue.trim();
    if (!trimmed || editingIndex === null) return;
    // Appends a new message rather than rewriting history, so it carries the
    // composer's staged attachments like any other send.
    void chat.sendMessage(trimmed);
    setEditingIndex(null);
    setEditValue("");
  }, [editValue, editingIndex, chat]);

  // A full-height state fills the scroll container by itself. The padded
  // wrapper below it then contributes its own 16px with nothing in it, which
  // is enough to make the container scroll — a scrollbar on a panel showing
  // nothing but a greeting. The wrapper stays mounted, because
  // `messagesEndRef` lives in it; it just stops reserving space it is not
  // using.
  const fullHeightState =
    isLoadingHistory || (messages.length === 0 && !isStreaming);

  return (
    <div ref={messagesContainerRef} className="flex-1 overflow-y-auto">
      {/* Full-height states render as direct children of the scroll container
          so their `h-full` resolves against its (flex-sized) height and centers
          vertically — nesting them in the padded wrapper would break that. */}
      {isLoadingHistory && (
        <div className="flex items-center justify-center h-full">
          <LoadingPlaceholder size="sm" />
        </div>
      )}
      {!isLoadingHistory && messages.length === 0 && !isStreaming && (
        <ChatbotEmptyState
          context={context}
          userGreetingName={userGreetingName}
          isDoctor={userGroup === AuthUserGroup.DOCTOR}
          onSelectSuggestion={onSelectSuggestion}
        />
      )}

      <div
        ref={messagesContentRef}
        className={`${mobile ? "px-2" : "px-4"} ${fullHeightState ? "" : "pt-3 pb-1"} space-y-3 ${fullscreen ? "mx-auto w-full max-w-[768px]" : ""}`}
      >
        {displayMessages.map((msg, idx) => (
          <div
            key={msg.renderKey ?? msg.messageId ?? idx}
            style={
              msg.role === "user" &&
              idx > 0 &&
              messages[idx - 1]?.role === "assistant"
                ? { marginTop: 24 }
                : undefined
            }
          >
            <div
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              {msg.role === "user" ? (
                (() => {
                  const hasUserText = !!msg.content && !msg.isContentSynthetic;
                  // A reloaded long message holds a preview plus its S3 copy;
                  // one sent this session still holds the full text.
                  const longAttachment = msg.attachments?.find(
                    (a) => a.source === "long_message",
                  );
                  const longSource: LongMessageSource | null = longAttachment
                    ? { attachment: longAttachment }
                    : msg.content.length > LONG_MESSAGE_THRESHOLD_CHARS
                      ? { text: msg.content }
                      : null;
                  const fileAttachments = msg.attachments?.filter(
                    (a) => a !== longAttachment,
                  );
                  const attachments =
                    fileAttachments && fileAttachments.length > 0 ? (
                      <div className="flex flex-col items-end gap-2">
                        {fileAttachments.map((att, attIdx) => {
                          const key = `${att.s3_key || att.filename}-${attIdx}`;
                          // Images speak for themselves — show the picture and
                          // put the filename in the hover tooltip. Documents
                          // keep the filename card; that's their content.
                          return att.previewUrl &&
                            att.content_type.startsWith("image/") ? (
                            <ChatAttachmentTile
                              key={key}
                              size="lg"
                              previewUrl={att.previewUrl}
                              filename={att.filename}
                            />
                          ) : (
                            <ChatPromptFile
                              key={key}
                              icon={renderAttachmentIcon(att)}
                              title={att.filename}
                            />
                          );
                        })}
                      </div>
                    ) : undefined;
                  // Editing would resend the preview in place of the text.
                  const canEdit =
                    msg.conversationId === conversationId &&
                    !isStreaming &&
                    !longSource;

                  if (editingIndex === idx) {
                    return (
                      <ChatPromptText
                        text={msg.content}
                        editing
                        editValue={editValue}
                        editMaxLength={MAX_INPUT_CHARS}
                        onEditChange={setEditValue}
                        onEditCancel={handleEditCancel}
                        onEditSend={handleEditSend}
                      />
                    );
                  }

                  if (!hasUserText) {
                    return attachments ? (
                      <div className="max-w-[85%]">{attachments}</div>
                    ) : null;
                  }

                  return (
                    <ChatPromptText
                      text={
                        longSource
                          ? `${msg.content.slice(0, LONG_MESSAGE_PREVIEW_CHARS)}…`
                          : msg.content
                      }
                      timestamp={msg.timestamp}
                      attachments={attachments}
                      onShowMore={
                        longSource
                          ? () => setLongMessage(longSource)
                          : undefined
                      }
                      copied={copiedIndex === idx}
                      // A preview row can't copy what it doesn't hold; the
                      // modal copies the full text.
                      onCopy={
                        longAttachment
                          ? undefined
                          : () => handleCopy(msg.content, idx)
                      }
                      onEdit={
                        canEdit
                          ? () => handleEditStart(idx, msg.content)
                          : undefined
                      }
                    />
                  );
                })()
              ) : (
                <AssistantMessage
                  msg={msg}
                  streaming={showStreaming && idx === messages.length}
                  activity={
                    showStreaming && idx === messages.length
                      ? streamingActivity
                      : undefined
                  }
                  markdownComponents={markdownComponents}
                  linkContext={linkContext}
                  onReport={canReport ? setFeedbackTarget : undefined}
                />
              )}
            </div>
          </div>
        ))}

        {/* Beneath the completed answer, not in it: compaction is session
            housekeeping, and its failure leaves the answer untouched. */}
        {isCompacting && (
          // pt-6 clears the answer's absolutely-positioned Copy/report row;
          // px-3 aligns with the answer text.
          <div
            className="flex items-center gap-1.5 px-3 pt-6 text-xs"
            style={{ color: "var(--indigo-text-secondary)" }}
            role="status"
          >
            <Icon name="spinner" sizeX={14} sizeY={14} />
            <span>Optimizing conversation memory…</span>
          </div>
        )}

        {/* Error display */}
        {error && (
          <div className="flex justify-center">
            <div
              className="rounded-lg px-4 py-2 text-sm flex items-center gap-2"
              style={{
                color: "var(--indigo-danger)",
                backgroundColor: "var(--indigo-danger-light, #fef2f2)",
              }}
            >
              <span>{error}</span>
              {!isStreaming && errorSource === "load" && (
                <Button
                  size="small"
                  type="link"
                  onClick={retryLoad}
                  style={{ color: "var(--indigo-danger)", padding: 0 }}
                >
                  Retry
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Warning display */}
        {warning && warning !== dismissedWarning && !error && (
          <div className="flex justify-center">
            <div
              className="rounded-lg px-4 py-2 text-sm flex items-center gap-2"
              style={{ color: "#92400e", backgroundColor: "#fffbeb" }}
            >
              <span>{warning}</span>
              <button
                onClick={() => setDismissedWarning(warning)}
                className="ml-1 hover:opacity-70"
                style={{
                  color: "#92400e",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "0 2px",
                  fontSize: "16px",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />

        {/* Scroll to bottom button */}
        {showScrollToBottom && (
          <div className="sticky bottom-2 flex justify-center pointer-events-none">
            <Button
              shape="circle"
              size="small"
              icon={<ArrowDownOutlined style={{ fontSize: 12 }} />}
              onClick={scrollToBottom}
              className="pointer-events-auto"
              style={{
                backgroundColor: "var(--indigo-gray-25)",
                border: "1px solid var(--indigo-border-default)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                color: "var(--indigo-text-secondary)",
              }}
            />
          </div>
        )}
      </div>

      <LongMessageModal
        source={longMessage}
        onClose={() => setLongMessage(null)}
      />
      <IndyFeedbackModal
        target={feedbackTarget}
        onClose={() => setFeedbackTarget(null)}
      />
    </div>
  );
};
